import type { Account, Category, CreditCard } from '@domain/finance'

/**
 * Os rótulos em português das uniões do domínio — código e campo em inglês,
 * tela em português (ADR-0009).
 *
 * `Record<União, string>` e não um `switch` com default: a união é fechada, e
 * um membro novo em `Account['type']` **não compila** até alguém dizer como ele
 * se chama na tela. Um default silencioso deixaria a etiqueta errada aparecer.
 */

export const ACCOUNT_TYPES: Record<Account['type'], string> = {
  checking: 'Conta corrente',
  savings: 'Poupança',
  payment: 'Conta de pagamento',
  cash: 'Dinheiro em espécie',
}

export const CARD_BRANDS: Record<CreditCard['brand'], string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  elo: 'Elo',
  amex: 'Amex',
  other: 'Outra',
}

export const CATEGORY_KINDS: Record<Category['kind'], string> = {
  expense: 'Despesa',
  income: 'Receita',
  transfer: 'Transferência',
}

/** `Account['status']` e `CreditCard['status']` são a mesma união. */
export const STATUSES: Record<Account['status'], string> = {
  active: 'Ativa',
  archived: 'Arquivada',
  sync_error: 'Erro de sincronização',
}

/** As chaves de um `Record` de união, tipadas — para alimentar um `<select>`. */
export function optionsOf<K extends string>(labels: Record<K, string>): K[] {
  return Object.keys(labels) as K[]
}

/**
 * Os doze meses em português, para o rótulo do seletor. **Tabela estática, e
 * não `Intl.DateTimeFormat`**: o lint proíbe o formatador de data fora de
 * `src/shared/date/` (decisão 79), porque sem `timeZone` explícito ele formata
 * no fuso de quem roda. Aqui não há instante nenhum a converter — `'2026-07'`
 * já é o mês —, então a tabela é a resposta certa e não um contorno.
 *
 * **Exportada** desde a Fase 4, porque `day-groups.ts` precisa do nome do mês
 * para o cabeçalho de dia (`'15 de julho'`). Uma segunda tabela ali seria dois
 * lugares para os doze meses divergirem — em português, com "março" acentuado,
 * é exatamente o tipo de lista que alguém recopia errado.
 */
export const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

/** `'2026-07'` → `'julho de 2026'`. Devolve o próprio mês se ele não tiver a
 * forma esperada — um rótulo feio é melhor do que uma tela que quebra. */
export function monthLabel(month: string): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1]
  return name === undefined ? month : `${name} de ${month.slice(0, 4)}`
}

/**
 * O rótulo curto do chip de mês: `'Julho'`, e `'Dez/25'` quando o ano difere do
 * corrente.
 *
 * **O ano só aparece quando distingue alguma coisa.** Repetir `2026` em quatro
 * chips lado a lado é ruído; omiti-lo em dezembro do ano passado, quando
 * janeiro está ao lado, é ambiguidade. A regra é essa, e o ano de referência
 * entra por parâmetro porque ler relógio aqui é proibido (decisão 79).
 */
export function shortMonthLabel(month: string, currentYear: string): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1]
  if (name === undefined) return month
  const year = month.slice(0, 4)
  const capitalizado = name.charAt(0).toUpperCase() + name.slice(1)
  return year === currentYear ? capitalizado : `${capitalizado.slice(0, 3)}/${year.slice(2)}`
}
